import { CSSObject } from '@emotion/react';
import { theme, zIndex } from 'styles';

const { color, radius } = theme;

const top: CSSObject = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 14,
  padding: '16px 16px 4px',
};

const thumbnail: CSSObject = {
  flex: 'none',
  width: 130,
  height: 130,
  objectFit: 'cover',
  borderRadius: radius.sm,
  background: color.grey75,
};

const summary: CSSObject = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  minWidth: 0,
};

const tag: CSSObject = {
  marginBottom: 6,
};

const title: CSSObject = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  wordBreak: 'break-all',
  fontSize: 15,
  fontWeight: 600,
  lineHeight: 1.4,
  color: color.grey800,
};

const startDate: CSSObject = {
  marginTop: 2,
  fontSize: 13,
  color: color.grey600,
};

const organization: CSSObject = {
  marginTop: 3,
  fontSize: 12,
  color: color.grey500,
};

const actions: CSSObject = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  marginTop: 12,
};

const subActions: CSSObject = {
  display: 'flex',
  gap: 8,
};

const actionButton: CSSObject = {
  flex: 1,
  width: '100%',
  height: 48,
};

const previewButton: CSSObject = {
  width: '100%',
  height: 48,
  marginTop: 12,
};

const sheet: CSSObject = {
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
  width: 500,
  maxWidth: '100%',
};

const field: CSSObject = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  input: {
    width: '100%',
  },
};

const label: CSSObject = {
  fontSize: 14,
  fontWeight: 600,
  color: color.text,
};

const help: CSSObject = {
  fontSize: 13,
  color: color.textMuted,
};

const quotaRow: CSSObject = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  fontSize: 14,
  color: color.textSub,
  span: {
    flex: 1,
    minWidth: 0,
  },
  input: {
    flex: 'none',
    width: 96,
  },
};

const saveButton: CSSObject = {
  width: '100%',
  height: 48,
};

const sheetTitle: CSSObject = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  paddingRight: 8,
};

const sheetSection: CSSObject = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

const answers: CSSObject = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  padding: '12px 14px',
  borderRadius: radius.md,
  background: color.grey50,
  fontSize: 14,
  lineHeight: 1.5,
  dt: {
    color: color.textSub,
  },
  dd: {
    marginTop: 2,
    color: color.text,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
};

const memo: CSSObject = {
  padding: '12px 14px',
  borderRadius: radius.md,
  background: color.grey50,
  fontSize: 14,
  lineHeight: 1.6,
  color: color.text,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
};

const attendeeRow: CSSObject = {
  display: 'flex',
  alignItems: 'center',
  borderBottom: `1px solid ${color.grey75}`,
  ':last-of-type': {
    borderBottom: 'none',
  },
  '> label': {
    flex: 'none',
    alignSelf: 'stretch',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 16,
  },
  '> button': {
    flex: 1,
    minWidth: 0,
    borderBottom: 'none',
    paddingLeft: 12,
  },
};

const selectionBar: CSSObject = {
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: zIndex.HEADER,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '12px 16px calc(12px + env(safe-area-inset-bottom))',
  background: color.white,
  boxShadow: `0 -1px 0 ${color.grey100}`,
  '> p': {
    flex: 1,
    fontSize: 14,
    fontWeight: 600,
    color: color.text,
  },
  '> button': {
    flex: 'none',
    height: 40,
  },
};

const selectionBarSpacer: CSSObject = {
  height: 'calc(64px + env(safe-area-inset-bottom))',
};

const search: CSSObject = {
  marginBottom: 12,
};

const nameWrap: CSSObject = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  minWidth: 0,
};

const name: CSSObject = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: 15,
  fontWeight: 600,
  color: color.text,
  span: {
    marginLeft: 4,
    fontWeight: 400,
    color: color.textSub,
  },
};

const meta: CSSObject = {
  fontSize: 13,
  color: color.textSub,
};

export {
  top,
  thumbnail,
  summary,
  tag,
  title,
  startDate,
  organization,
  actions,
  subActions,
  actionButton,
  previewButton,
  sheet,
  field,
  label,
  help,
  quotaRow,
  saveButton,
  sheetTitle,
  sheetSection,
  answers,
  memo,
  attendeeRow,
  selectionBar,
  selectionBarSpacer,
  search,
  nameWrap,
  name,
  meta,
};
